import { LoadingOutlined } from '@ant-design/icons'
import {
  BBDTag,
  GraviButton,
  Horizontal,
  MaterialIcon,
  type MaterialSymbolIcon,
  Texto,
  Vertical,
} from '@gravitate-js/excalibrr'
import { Tabs, Tooltip, Spin } from 'antd'
import type { TabsProps } from 'antd'
import { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import type { MatchStatus } from '../../../../api/useSupplierReconciliation/types/common'
import type { MatchOverviewResponse } from '../../../../api/useSupplierReconciliation/types/match'
import { MATCH_STATUS_CONFIG } from '../constants/statusConfig'

interface TabStatusItem {
  key: string
  label: string
  icon: string | MaterialSymbolIcon
  display: string
  tagProps?: {
    error?: boolean
    warning?: boolean
    success?: boolean
    theme2?: boolean
  }
}

interface MatchViewHeaderProps {
  activeStatus: MatchStatus | 'all'
  setActiveStatus: (status: MatchStatus | 'all') => void
  statusCounts: Record<string, number> & {
    isLoading?: Record<string, boolean>
  }
  isLoading: boolean
  isVoidedViewActive: boolean
  setIsVoidedViewActive: (active: boolean) => void
  filteredData?: MatchOverviewResponse
}

export function MatchViewHeader({
  activeStatus,
  setActiveStatus,
  statusCounts,
  isLoading,
  isVoidedViewActive,
  setIsVoidedViewActive,
  filteredData,
}: MatchViewHeaderProps) {
  const navigate = useNavigate()

  const statusTabItems: TabsProps['items'] = useMemo(() => {
    const orderedTabConfigs: TabStatusItem[] = [
      {
        key: 'all',
        label: 'All Open',
        icon: 'list',
        display: 'All Open',
      },
      {
        key: 'unmatched',
        label: MATCH_STATUS_CONFIG.unmatched.display,
        icon: MATCH_STATUS_CONFIG.unmatched.icon as MaterialSymbolIcon,
        display: MATCH_STATUS_CONFIG.unmatched.display,
        tagProps: MATCH_STATUS_CONFIG.unmatched.tagProps,
      },
      {
        key: 'hold',
        label: MATCH_STATUS_CONFIG.hold.display,
        icon: MATCH_STATUS_CONFIG.hold.icon as MaterialSymbolIcon,
        display: MATCH_STATUS_CONFIG.hold.display,
        tagProps: MATCH_STATUS_CONFIG.hold.tagProps,
      },
      {
        key: 'unapproved',
        label: MATCH_STATUS_CONFIG.unapproved.display,
        icon: MATCH_STATUS_CONFIG.unapproved.icon as MaterialSymbolIcon,
        display: MATCH_STATUS_CONFIG.unapproved.display,
        tagProps: MATCH_STATUS_CONFIG.unapproved.tagProps,
      },
      {
        key: 'approved',
        label: MATCH_STATUS_CONFIG.approved.display,
        icon: MATCH_STATUS_CONFIG.approved.icon as MaterialSymbolIcon,
        display: MATCH_STATUS_CONFIG.approved.display,
        tagProps: MATCH_STATUS_CONFIG.approved.tagProps,
      },
    ]

    return orderedTabConfigs.map((statusConfig) => ({
      key: statusConfig.key,
      label: (
        <Horizontal verticalCenter horizontalCenter style={{ gap: '8px' }}>
          <MaterialIcon icon={statusConfig.icon as MaterialSymbolIcon} />
          <Texto appearance='default'>{statusConfig.display}</Texto>
          <BBDTag
            error={statusCounts[statusConfig.key] > 0 ? statusConfig.tagProps?.error : false}
            warning={statusCounts[statusConfig.key] > 0 ? statusConfig.tagProps?.warning : false}
            success={statusCounts[statusConfig.key] > 0 ? statusConfig.tagProps?.success : false}
            theme2={statusCounts[statusConfig.key] > 0 ? (statusConfig.tagProps?.theme2 ?? false) : false}
            style={{ width: 'fit-content' }}
          >
            {statusCounts.isLoading?.[statusConfig.key] ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} />
            ) : (
              statusCounts[statusConfig.key] || 0
            )}
          </BBDTag>
        </Horizontal>
      ),
      children: null,
    }))
  }, [statusCounts, isLoading])

  return (
    <Tabs
      activeKey={activeStatus ?? undefined}
      onChange={(key) => setActiveStatus(key as MatchStatus | 'all')}
      className='px-4'
      style={{
        marginBottom: 0,
        backgroundColor: 'var(--bg-1)',
        borderTop: '1px solid var(--gray-200)',
        borderRight: '1px solid var(--gray-200)',
        borderLeft: '1px solid var(--gray-200)',
        borderRadius: '8px 8px 0 0',
      }}
      tabBarStyle={{
        marginBottom: 0,
      }}
      items={statusTabItems}
      tabBarExtraContent={
        <Horizontal gap={8}>
          {/* Void Button */}
          <GraviButton
            type='default'
            theme2={isVoidedViewActive}
            disabled={(statusCounts.void || 0) === 0}
            icon={<MaterialIcon icon='cancel' className='pt-1' />}
            onClick={() => setIsVoidedViewActive(!isVoidedViewActive)}
            size='small'
          >
            Void (
            {statusCounts.isLoading?.void ? (
              <Spin indicator={<LoadingOutlined style={{ fontSize: 12 }} spin />} />
            ) : (
              statusCounts.void || 0
            )}
            )
          </GraviButton>

          {/* Unmapped Button */}
          <Tooltip
            title={
              <Vertical gap={12}>
                <Texto variant='h6' style={{ color: 'white' }}>
                  Unmapped Suppliers
                </Texto>

                <Horizontal gap={24}>
                  <Vertical gap={4}>
                    <Texto variant='body2' style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Suppliers
                    </Texto>
                    <Texto variant='h5' weight='bold' style={{ color: 'white' }}>
                      {filteredData?.unmapped_supplier_names?.length || 0}
                    </Texto>
                  </Vertical>
                  <Vertical gap={4}>
                    <Texto variant='body2' style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Invoices
                    </Texto>
                    <Texto variant='h5' weight='bold' style={{ color: 'white' }}>
                      {filteredData?.unmapped_invoice_count || 0}
                    </Texto>
                  </Vertical>
                </Horizontal>

                {filteredData?.unmapped_supplier_names && filteredData.unmapped_supplier_names.length > 0 ? (
                  <Vertical gap={8}>
                    <Texto variant='body2' style={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                      Suppliers needing mapping:
                    </Texto>
                    <Vertical gap={4}>
                      {filteredData.unmapped_supplier_names.slice(0, 5).map((supplierName, index) => (
                        <Texto
                          key={index}
                          variant='body2'
                          style={{
                            color: 'white',
                            padding: '4px 8px',
                            backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            borderRadius: '4px',
                          }}
                        >
                          " {supplierName}
                        </Texto>
                      ))}
                      {filteredData.unmapped_supplier_names.length > 5 && (
                        <Texto
                          variant='body2'
                          style={{
                            color: 'rgba(255, 255, 255, 0.8)',
                            fontStyle: 'italic',
                          }}
                        >
                          +{filteredData.unmapped_supplier_names.length - 5} more...
                        </Texto>
                      )}
                    </Vertical>
                  </Vertical>
                ) : (
                  <Texto
                    variant='body2'
                    style={{
                      color: 'rgba(255, 255, 255, 0.8)',
                      fontStyle: 'italic',
                    }}
                  >
                    No unmapped suppliers found
                  </Texto>
                )}
              </Vertical>
            }
          >
            <GraviButton
              type='default'
              error={(filteredData?.unmapped_supplier_names?.length || 0) > 0}
              disabled={(filteredData?.unmapped_supplier_names?.length || 0) === 0}
              icon={<MaterialIcon icon='warning' className='pt-1' />}
              onClick={() => navigate('/supplier_rec/supplier_configs')}
              size='small'
            >
              Unmapped ({filteredData?.unmapped_supplier_names?.length || 0})
            </GraviButton>
          </Tooltip>
        </Horizontal>
      }
    />
  )
}
